import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import jwt_decode from 'jwt-decode';
import { expandRolePermissions } from '../config/permissions';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverPermissions, setServerPermissions] = useState(null);
  const interceptorsInitializedRef = useRef(false);

  // Configure axios defaults. Set REACT_APP_API_URL to your Laravel domain + /api, e.g. https://tu-dominio.com/api
  // Default to local backend during development if REACT_APP_API_URL is not set
  // Default to production API when no REACT_APP_API_URL is provided during dev
  const API_BASE = (process.env.REACT_APP_API_URL || 'https://qualityapi.dhp-novolex.com/api').replace(/\/$/, '');
  axios.defaults.baseURL = API_BASE;
  // Explicit CORS/credentials behaviour: set to false unless your API uses cookies/sessions
  axios.defaults.withCredentials = false;

  useEffect(() => {
    if (interceptorsInitializedRef.current) return;
    interceptorsInitializedRef.current = true;

    // REQUEST INTERCEPTOR: Add Authorization header to every request
    const reqId = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // RESPONSE INTERCEPTOR
    const resId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          localStorage.removeItem('refresh_token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          setServerPermissions(null);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
      interceptorsInitializedRef.current = false;
    };
  }, []);

  // Set up axios default auth header from localStorage token and restore user
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');
    
    // Helper function to check if JWT token is expired
    const isTokenExpired = (jwtToken) => {
      try {
        const decoded = jwt_decode(jwtToken);
        if (!decoded.exp) {
          return true;
        }
        // exp is in seconds, current time is in milliseconds
        const expirationTime = decoded.exp * 1000;
        const currentTime = Date.now();
        return currentTime > expirationTime;
      } catch (error) {
        return true;
      }
    };
    
    if (token && !isTokenExpired(token)) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      try {
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        } else {
          const decoded = jwt_decode(token);
          setUser({
            username: decoded.username || decoded.sub,
            email: decoded.email,
            role: decoded.role
          });
        }
      } catch (error) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        localStorage.removeItem('refresh_token');
      }
    } else if (token && isTokenExpired(token)) {
      // Token exists but is expired - clean up
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      localStorage.removeItem('refresh_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    }
    
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      const { access_token, user: userInfo } = response.data; // Laravel returns access_token and user

      // Store token
      localStorage.setItem('access_token', access_token);

      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Prefer server-provided user info; fallback to token claims
      let nextUser = userInfo;
      if (!nextUser) {
        try {
          const decoded = jwt_decode(access_token);
          nextUser = { 
            username: decoded.username || decoded.sub || email, 
            email: decoded.email || email,
            role: decoded.role 
          };
        } catch (e) {
          nextUser = { username: email, email, role: undefined };
        }
      }
      setUser(nextUser);
      localStorage.setItem('user', JSON.stringify(nextUser));

      toast.success('Inicio de sesión exitoso');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setServerPermissions(null);
    toast.success('Sesión cerrada');
  };

  const isAuthenticated = () => user !== null;
  const hasRole = (role) => user?.role === role || user?.role === 'admin_master';
  const hasAnyRole = (roles) => roles.includes(user?.role) || user?.role === 'admin_master';

  const refreshPermissions = React.useCallback(async () => {
    if (!user || !localStorage.getItem('access_token')) {
      setServerPermissions(null);
      return [];
    }
    try {
      const { data } = await axios.get('/permissions/current');
      const permissions = Array.isArray(data?.permissions) ? data.permissions : [];
      setServerPermissions(permissions);
      return permissions;
    } catch (error) {
      setServerPermissions(null);
      return [];
    }
  }, [user]);

  useEffect(() => {
    if (user) refreshPermissions();
    else setServerPermissions(null);
  }, [refreshPermissions, user]);

  // Server permissions are authoritative; defaults are a short-lived fallback while loading.
  const rolePermissions = React.useMemo(() =>
    serverPermissions ?? (user?.role ? expandRolePermissions(user.role) : []),
    [serverPermissions, user?.role]
  );
  
  const can = React.useCallback((permission) => {
    if (!permission) return true;
    return rolePermissions.includes('*') || rolePermissions.includes(permission);
  }, [rolePermissions]);
  
  const canAny = React.useCallback((permissions) => {
    if (!permissions || permissions.length === 0) return true;
    return rolePermissions.includes('*') || permissions.some((permission) => rolePermissions.includes(permission));
  }, [rolePermissions]);

  const isAnalyst = () => user?.role === 'analyst';
  const isAdminMaster = () => user?.role === 'admin_master';
  const isAdminSite = () => user?.role === 'admin_site';
  const isAdminAnalytics = () => user?.role === 'admin_analytics';
  const isAdmin = () => ['admin_master', 'admin_site', 'admin_analytics'].includes(user?.role);
  
  const value = { 
    user, 
    loading, 
    login, 
    logout, 
    isAuthenticated, 
    hasRole, 
    hasAnyRole, 
    isAnalyst,
    isAdminMaster,
    isAdminSite,
    isAdminAnalytics,
    isAdmin,
    rolePermissions,
    refreshPermissions,
    can,
    canAny
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
